import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/contexts/AuthContext";
import { MapTrifold, Plus, Trash, Plant } from "@phosphor-icons/react";

export default function FieldsPage() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", size_ha: "", crop: "", notes: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/app/fields`);
      setFields(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const captureLocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { timeout: 8000 }
      );
    });

  const onCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const loc = await captureLocation();
      const payload = {
        name: form.name,
        size_ha: form.size_ha ? parseFloat(form.size_ha) : null,
        crop: form.crop || null,
        notes: form.notes || null,
        lat: loc?.lat ?? null,
        lng: loc?.lng ?? null,
      };
      await axios.post(`${API}/app/fields`, payload);
      toast.success("Fläche angelegt");
      setForm({ name: "", size_ha: "", crop: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Anlegen");
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Fläche wirklich löschen?")) return;
    await axios.delete(`${API}/app/fields/${id}`);
    toast.success("Fläche gelöscht");
    load();
  };

  return (
    <div className="max-w-6xl">
      <h1 className="font-heading text-3xl font-semibold text-forest-900 mb-2">Flächen</h1>
      <p className="font-body text-[15px] text-[#4A524C] mb-8">
        Verwalten Sie Ihre bewirtschafteten Flächen. GPS-Position wird beim
        Anlegen automatisch erfasst.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Form */}
        <form
          onSubmit={onCreate}
          data-testid="field-form"
          className="lg:col-span-2 bg-white rounded-3xl border border-bone-200 p-6 sm:p-7 space-y-4"
        >
          <h2 className="font-heading text-lg font-semibold text-forest-900 flex items-center gap-2">
            <Plus size={18} weight="bold" /> Neue Fläche
          </h2>
          <Input
            label="Bezeichnung"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
            testId="field-name"
          />
          <Input
            label="Größe (ha)"
            type="number"
            step="0.01"
            value={form.size_ha}
            onChange={(v) => setForm({ ...form, size_ha: v })}
            testId="field-size"
          />
          <Input
            label="Kultur"
            value={form.crop}
            onChange={(v) => setForm({ ...form, crop: v })}
            placeholder="z. B. Winterweizen"
            testId="field-crop"
          />
          <TextArea
            label="Notizen"
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
            testId="field-notes"
          />
          <button
            type="submit"
            disabled={creating}
            data-testid="field-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-forest-600 hover:bg-forest-700 disabled:opacity-60 text-white font-medium px-5 py-3 rounded-xl transition"
          >
            <Plus size={16} weight="bold" />
            {creating ? "Speichere …" : "Fläche anlegen"}
          </button>
        </form>

        {/* List */}
        <div className="lg:col-span-3">
          {loading ? (
            <div className="font-body text-sm text-[#7A827C]">Lade Flächen …</div>
          ) : fields.length === 0 ? (
            <div className="bg-white rounded-3xl border border-bone-200 p-10 text-center">
              <Plant size={28} weight="duotone" className="text-forest-700 mx-auto mb-3" />
              <div className="font-heading text-lg font-semibold text-forest-900">
                Noch keine Flächen
              </div>
              <div className="font-body text-sm text-[#4A524C] mt-1">
                Legen Sie links Ihre erste Fläche an.
              </div>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="field-list">
              {fields.map((f) => (
                <li
                  key={f.id}
                  data-testid={`field-row-${f.id}`}
                  className="bg-white rounded-2xl border border-bone-200 p-5 flex items-start gap-4"
                >
                  <div className="w-11 h-11 rounded-xl bg-forest-50 border border-forest-100 flex items-center justify-center flex-shrink-0">
                    <MapTrifold size={20} weight="duotone" className="text-forest-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-lg font-semibold text-forest-900 truncate">
                      {f.name}
                    </div>
                    <div className="font-body text-sm text-[#4A524C] flex flex-wrap gap-x-4 gap-y-1 mt-1">
                      {f.crop && <span>{f.crop}</span>}
                      {f.size_ha != null && <span>{f.size_ha} ha</span>}
                      {f.lat != null && (
                        <span className="text-[#7A827C]">
                          {f.lat.toFixed(4)}, {f.lng.toFixed(4)}
                        </span>
                      )}
                    </div>
                    {f.notes && (
                      <div className="mt-2 text-sm text-[#4A524C] line-clamp-2">{f.notes}</div>
                    )}
                  </div>
                  <button
                    onClick={() => onDelete(f.id)}
                    data-testid={`field-delete-${f.id}`}
                    className="p-2 text-[#7A827C] hover:text-red-600 transition"
                    aria-label="Löschen"
                  >
                    <Trash size={18} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

const Input = ({ label, testId, onChange, ...props }) => (
  <div>
    <label className="block font-body text-sm font-medium text-forest-900 mb-1.5">
      {label}
    </label>
    <input
      data-testid={testId}
      onChange={(e) => onChange?.(e.target.value)}
      className="w-full font-body text-[15px] px-3.5 py-2.5 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15 transition"
      {...props}
    />
  </div>
);

const TextArea = ({ label, testId, onChange, value, ...props }) => (
  <div>
    <label className="block font-body text-sm font-medium text-forest-900 mb-1.5">
      {label}
    </label>
    <textarea
      data-testid={testId}
      onChange={(e) => onChange?.(e.target.value)}
      value={value}
      rows={3}
      className="w-full font-body text-[15px] px-3.5 py-2.5 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15 transition resize-none"
      {...props}
    />
  </div>
);
